import type {
  AdminCatalogMediaListInput,
  AdminCatalogMediaSummary,
  AttachCatalogMediaInput,
  CatalogMediaOwnerMutationResult,
  CatalogMediaUploadInitiation,
  DetachCatalogMediaInput,
  InitiateCatalogMediaUploadInput,
  PaginatedResult,
} from '@emazy/shared-types';
import {
  adminCatalogMediaListSchema,
  attachCatalogMediaSchema,
  detachCatalogMediaSchema,
  initiateCatalogMediaUploadSchema,
} from '@emazy/shared-validation';
import type { z } from 'zod';

import { ApiClient } from '@/lib/api';
import {
  adminMediaSchema,
  initiationSchema,
  mediaPageSchema,
  ownerMutationSchema,
} from './schemas';
import { CatalogMediaClientError, type AdminCatalogMediaApi } from './types';

export class HttpAdminCatalogMediaApi implements AdminCatalogMediaApi {
  constructor(private readonly client: ApiClient) {}

  initiate(input: InitiateCatalogMediaUploadInput) {
    return this.mutate(
      '/v1/admin/catalog/media/uploads',
      'POST',
      initiateCatalogMediaUploadSchema.parse(input),
      initiationSchema,
    ) as Promise<CatalogMediaUploadInitiation>;
  }
  list(input: AdminCatalogMediaListInput, signal?: AbortSignal) {
    const parsed = adminCatalogMediaListSchema.parse(input);
    return this.request(
      `/v1/admin/catalog/media${query(parsed)}`,
      mediaPageSchema,
      signal ? { signal } : {},
    ) as Promise<PaginatedResult<AdminCatalogMediaSummary>>;
  }
  get(id: string, signal?: AbortSignal) {
    return this.request(
      `/v1/admin/catalog/media/${encodeURIComponent(id)}`,
      adminMediaSchema,
      signal ? { signal } : {},
    ) as Promise<AdminCatalogMediaSummary>;
  }
  retry(id: string) {
    return this.mutate(
      `/v1/admin/catalog/media/${encodeURIComponent(id)}/retry`,
      'POST',
      {},
      adminMediaSchema,
    ) as Promise<AdminCatalogMediaSummary>;
  }
  delete(id: string) {
    return this.request(`/v1/admin/catalog/media/${encodeURIComponent(id)}`, adminMediaSchema, {
      method: 'DELETE',
    }) as Promise<AdminCatalogMediaSummary>;
  }
  attach(id: string, input: AttachCatalogMediaInput) {
    return this.mutate(
      `/v1/admin/catalog/media/${encodeURIComponent(id)}/attach`,
      'POST',
      attachCatalogMediaSchema.parse(input),
      ownerMutationSchema,
    ) as Promise<CatalogMediaOwnerMutationResult>;
  }
  detach(id: string, input: DetachCatalogMediaInput) {
    return this.mutate(
      `/v1/admin/catalog/media/${encodeURIComponent(id)}/detach`,
      'POST',
      detachCatalogMediaSchema.parse(input),
      ownerMutationSchema,
    ) as Promise<CatalogMediaOwnerMutationResult>;
  }

  private mutate(path: string, method: 'POST', body: unknown, schema: z.ZodType) {
    return this.request(path, schema, { method, body: JSON.stringify(body) });
  }
  private async request(path: string, schema: z.ZodType, options: RequestInit = {}) {
    const headers = new Headers(options.headers);
    if (options.body) headers.set('content-type', 'application/json');
    let response: Response;
    try {
      response = await this.client.request(path, { ...options, headers, auth: 'protected' });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new CatalogMediaClientError('network-error', 'The media service could not be reached.');
    }
    const envelope = (await response.json().catch(() => ({}))) as {
      data?: unknown;
      error?: { code?: string; message?: string };
    };
    if (!response.ok)
      throw new CatalogMediaClientError(
        envelope.error?.code ?? 'unknown',
        safeMessage(envelope.error?.code),
        response.status,
      );
    try {
      return schema.parse(envelope.data);
    } catch {
      throw new CatalogMediaClientError(
        'invalid-response',
        'The media service returned invalid data.',
      );
    }
  }
}

function query(input: Record<string, unknown>) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(input))
    if (value !== undefined && value !== '') params.set(key, String(value));
  return params.size ? `?${params}` : '';
}

function safeMessage(code?: string) {
  const messages: Record<string, string> = {
    'media-too-large': 'The selected image exceeds the 20 MiB limit.',
    'media-not-ready': 'Wait for image processing to finish before attaching it.',
    'media-attached': 'Detach this image before deleting it.',
    'media-already-attached': 'This image is already attached.',
    'media-retry-not-allowed': 'This failure cannot be retried.',
    'owner-version-conflict': 'The product or category changed in another session.',
    'rate-limit-exceeded': 'The hourly upload limit has been reached.',
  };
  return (code && messages[code]) ?? 'The media operation could not be completed.';
}
